import React from "react";
import { Box, Button, Typography, Grid, Card, CardContent } from "@mui/material";
import NavBar from "../components/NavigationBar";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations/translations";
import platypus from "../img/37302831-6ae4-417f-8308-a32467993ff9.png";
import ScrollFadeSection from "../components/ScrollFadeSection";

const LandingPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language].landing;

  const squadImages = [
    require("../img/Perry 3@2x.png"),
    require("../img/Frame 10.png"),
    require("../img/Adobe Express - file.png"),
  ];

  return (
    <>
      <NavBar />

      {/* ---------- HERO SECTION ---------- */}
      <ScrollFadeSection>
        <Box
          sx={{
            mt: 4,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
          }}
        >
          <Grid container spacing={6} alignItems="center" sx={{ maxWidth: "1300px" }}>
            {/* Left */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "inline-block",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  border: "2px solid #5B8CB9",
                  color: "#5B8CB9",
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                {t.welcome}
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Bangers",
                  fontSize: "3.2rem",
                  lineHeight: 1.1,
                  mb: 3,
                }}
              >
                {t.title}
              </Typography>

              <Typography sx={{ mb: 4, fontSize: "1.1rem", maxWidth: "500px" }}>
                {t.subtitle}
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#5B8CB9",
                    color: "white",
                    borderRadius: "30px",
                    px: 4,
                    py: 1.5,
                    "&:hover": { backgroundColor: "#4C7AA0" },
                  }}
                  onClick={() => (window.location.href = "/quiz")}
                >
                  {t.startButton}
                </Button>

                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: "30px",
                    px: 4,
                    py: 1.5,
                    color: "#5B8CB9",
                    borderColor: "#5B8CB9",
                    "&:hover": { borderColor: "#4C7AA0", color: "#4C7AA0" },
                  }}
                  onClick={() => (window.location.href = "/sources")}
                >
                  {t.learnMoreButton}
                </Button>
              </Box>
            </Grid>

            {/* Right image */}
            <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={platypus}
                alt="Platypus"
                sx={{
                  width: "80%",
                  maxWidth: "450px",
                  backgroundColor: "transparent",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </ScrollFadeSection>

      {/* ---------- WHAT’S THE FUSS SECTION ---------- */}
      <ScrollFadeSection>
        <Box sx={{ textAlign: "center", py: 12, backgroundColor: "#fafafa" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Bangers", mb: 1, fontSize: "2.7rem" }}
          >
            {t.whatsTheFuss}
          </Typography>

          <Box
            sx={{ width: "60px", height: "4px", background: "#5B8CB9", mx: "auto", mb: 6 }}
          />

          <Grid container spacing={4} justifyContent="center">
            {[
              {
                icon: "🔎",
                title: t.titleFussCard1,
                text: t.descriptionFussCard1,
              },
              {
                icon: "💡",
                title: t.titleFussCard2,
                text: t.descriptionFussCard2,
              },
              {
                icon: "😄",
                title: t.titleFussCard3,
                text: t.descriptionFussCard3,
              },
            ].map((item, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Card
                  sx={{
                    borderRadius: "20px",
                    border: "2px solid #5B8CB9",
                    p: 2,
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography sx={{ fontSize: "2rem" }}>{item.icon}</Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontFamily: "Bangers", mb: 2, mt: 1 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography>{item.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </ScrollFadeSection>

      {/* ---------- MEET THE SQUAD SECTION ---------- */}
      <ScrollFadeSection>
        <Box sx={{ textAlign: "center", py: 12, mx: { xs: 2, md: 10 } }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Bangers", mb: 1, fontSize: "2.7rem" }}
          >
            {t.meetTheSquad}
          </Typography>

          <Box
            sx={{ width: "60px", height: "4px", background: "#5B8CB9", mx: "auto", mb: 6 }}
          />

          {/* --- Character Images --- */}

          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: "🦆", name: t.titleTeamMember1, role: t.descriptionTeamMember1 },
              { icon: "🌭", name: t.titleTeamMember2, role: t.descriptionTeamMember2 },
              { icon: "🐹", name: t.titleTeamMember3, role: t.descriptionTeamMember3 },
            ].map((person, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Card
                  sx={{
                    position: "relative",
                    borderRadius: "20px",
                    border: "3px solid #5B8CB9", 
                    overflow: "hidden",
                    height: 280,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  }}
                >
                  {/* Hintergrundbild */}
                  <Box
                    component="img"
                    src={squadImages[i]}
                    alt={person.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      filter: "brightness(0.75)",
                    }}
                  />

                  {/* Overlay Gradient */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 2,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.05))",
                      color: "white",
                    }}
                  >
                    {/* Icon + Name */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: "1.7rem" }}>{person.icon}</Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontFamily: "Bangers", fontSize: "1.3rem" }}
                      >
                        {person.name}
                      </Typography>
                    </Box>

                    {/* Role */}
                    <Typography sx={{ fontSize: "0.9rem", opacity: 0.9 }}>
                      {person.role}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </ScrollFadeSection>


      {/* ---------- CTA SECTION ---------- */}
      <ScrollFadeSection>
        <Box
          sx={{
            background: "linear-gradient(180deg, #5B8CB9, #B3DBF1)",
            textAlign: "center",
            py: 10,
            color: "white",
          }}
        >
          <Typography variant="h3" sx={{ fontFamily: "Bangers", mb: 2 }}>
            {t.titleCTA}
          </Typography>

          <Typography sx={{ mb: 4, opacity: 0.9 }}>
            {t.descriptionCTA}
          </Typography>

          <Button
            variant="contained"
            sx={{
              borderRadius: "30px",
              px: 5,
              py: 1.5,
              backgroundColor: "white",
              color: "#5B8CB9",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#f3f3f3" },
            }}
          >
            {t.ctaButton}
          </Button>
        </Box>
      </ScrollFadeSection>

      <Footer />
    </>
  );
};

export default LandingPage;
